import React, { useEffect } from 'react';
import { translations } from '../translations';
import ContactButton from './contact/ContactButton';

interface ComingSoonUIProps {
  language: string;
  onLanguageChange: (lang: string) => void;
}

const ComingSoonUI: React.FC<ComingSoonUIProps> = ({ language, onLanguageChange }) => {
  const t = translations[language];

  useEffect(() => {
    if (language === 'fr') {
      document.title = 'Dingo – Mobilité urbaine intelligente';
    } else {
      document.title = 'Dingo – Smart Urban Mobility';
    }
  }, [language]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-4 overflow-hidden font-sf-pro-text">
      {/* Logo */}
      <div className="mb-16 float">
        <img 
          src={process.env.PUBLIC_URL + '/assets/img/dingo-icon.png'} 
          alt="Dingo" 
          className="h-12 w-auto object-contain" 
        />
      </div>

      {/* Main Content */}
      <div className="text-center max-w-2xl mx-auto relative z-10">
        {/* Rocket emoji */}
        <div className="mb-6 float">
          <span role="img" aria-label="rocket" className="text-4xl">🚀</span>
        </div>

        {/* Text Content */}
        <h2 className="text-[#6B7280] text-xl mb-4 font-sf-pro-display">{t.comingSoon}</h2>
        <h1 className="text-[#4F46E5] text-4xl md:text-5xl font-bold mb-6 font-sf-pro-display">
          <span className="italic">{t.tagline}</span><br/>
          <span className="text-[#F59E0B]">Go Dingo</span>
        </h1>
        <p className="text-[#6B7280] text-lg mb-8">
          {t.description1}<br />
          {t.description2}
        </p>

        {/* Notify Me Button */}
        <div className="flex justify-center">
          <ContactButton language={language} />
        </div>

        {/* Social Links */}
        <div className="mt-16 flex justify-center space-x-4">
          {/* TikTok */}
          <a href="https://tiktok.com" className="p-3 bg-white shadow-sm rounded-full hover:bg-gray-50 transition-colors hover:scale-110 transform duration-200">
            <svg className="h-5 w-5 text-[#1E293B]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </a>
          {/* Instagram */}
          <a href="https://instagram.com" className="p-3 bg-white shadow-sm rounded-full hover:bg-gray-50 transition-colors hover:scale-110 transform duration-200">
            <svg className="h-5 w-5 text-[#1E293B]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          {/* LinkedIn */}
          <a href="https://linkedin.com" className="p-3 bg-white shadow-sm rounded-full hover:bg-gray-50 transition-colors hover:scale-110 transform duration-200">
            <svg className="h-5 w-5 text-[#1E293B]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
          </a>
          {/* Messenger */}
          <a href="https://messenger.com" className="p-3 bg-white shadow-sm rounded-full hover:bg-gray-50 transition-colors hover:scale-110 transform duration-200">
            <svg className="h-5 w-5 text-[#1E293B]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.486 2 2 6.262 2 11.5c0 2.545 1.088 4.988 3 6.772v4.228l4.135-2.525c.912.25 1.878.38 2.865.38 5.514 0 10-4.262 10-9.5S17.514 2 12 2zm1.003 12.508l-2.955-3.129-5.744 3.129 6.312-6.609 3.029 3.129 5.674-3.129-6.316 6.609z"/>
            </svg>
          </a>
        </div>
      </div>

      {/* Language Switcher */}
      <div className="fixed top-4 right-4 flex space-x-2">
        <button 
          onClick={() => onLanguageChange('fr')}
          className={`px-3 py-1 rounded-md ${language === 'fr' ? 'bg-[#4F46E5] text-white' : 'bg-white text-[#1E293B]'}`}
        >
          FR
        </button>
        <button 
          onClick={() => onLanguageChange('en')}
          className={`px-3 py-1 rounded-md ${language === 'en' ? 'bg-[#4F46E5] text-white' : 'bg-white text-[#1E293B]'}`}
        >
          EN
        </button>
      </div>

      {/* Decorative Elements */}
      <div className="fixed left-10 bottom-10">
        <div className="w-8 h-8 bg-[#10B981] rounded-full opacity-50 decorative-element"></div>
      </div>
      <div className="fixed right-10 bottom-10">
        <div className="w-16 h-16 bg-[#F59E0B] rounded-full opacity-50 decorative-element"></div>
      </div>
    </div>
  );
};

export default ComingSoonUI; 